import client from './client';

interface JobResponse {
  job_id?: string;
  job_ids?: string[];
  status: 'pending';
  message: string;
  status_url?: string;
  is_batch?: boolean;
}

interface JobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: any;
  error_message?: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
}

interface BatchJobStatus {
  jobs: JobStatus[];
  all_completed: boolean;
  any_failed: boolean;
  total_jobs: number;
}

async function executeSimStudioWorkflow({
  promptText,
  type,
  documentId,
}: {
  promptText: string;
  type:
    | 'pawGen'
    | 'pawSingleRegen'
    | 'conopPhase1Gen'
    | 'iraGen'
    | 'iraItemRegen'
    | 'ppagRegen'
    | 'ppagGen'
    | 'conopFinalGen'
    | 'igGen';
  documentId: string;
}) {
  const { data: jobResponse } = await client.post<JobResponse>(
    `/simstudio/execute-workflow/`,
    {
      prompt_text: promptText,
      type,
      document_id: documentId,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  // Handle batch jobs (multiple job IDs)
  if (jobResponse.is_batch && jobResponse.job_ids) {
    const result = await pollBatchJobStatus(jobResponse.job_ids);
    return result;
  }

  // Handle single job
  const result = await pollJobStatus(jobResponse.job_id!);
  return result;
}

async function pollJobStatus(jobId: string): Promise<any> {
  const maxPollTime = 20 * 60 * 1000;
  const pollInterval = 3000;
  const startTime = Date.now();

  while (Date.now() - startTime < maxPollTime) {
    let status: JobStatus;

    try {
      const response = await client.get<JobStatus>(`/simstudio/job-status/${jobId}`);
      status = response.data;
    } catch (error) {
      if (Date.now() - startTime >= maxPollTime) {
        throw new Error('Job polling timeout');
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      continue;
    }

    if (status.status === 'completed') {
      return status.result;
    } else if (status.status === 'failed') {
      throw new Error(status.error_message || 'Job failed');
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  throw new Error('Job polling timeout - the job may still be processing');
}

async function pollBatchJobStatus(jobIds: string[]): Promise<any> {
  const maxPollTime = 30 * 60 * 1000;
  const perJobTimeout = 5 * 60 * 1000;
  const pollInterval = 3000;
  const startTime = Date.now();
  const jobProcessingStartTimes = new Map<string, number>();

  while (Date.now() - startTime < maxPollTime) {
    let batchStatus: BatchJobStatus;

    try {
      const response = await client.post<BatchJobStatus>(`/simstudio/batch-job-status`, {
        job_ids: jobIds,
      });
      batchStatus = response.data;
    } catch (error: any) {
      console.error('Error polling batch job status:', error.message || error);
      if (Date.now() - startTime >= maxPollTime) {
        throw new Error('Batch job polling timeout');
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      continue;
    }

    const currentTime = Date.now();

    batchStatus.jobs.forEach((job) => {
      if (job.status === 'processing' && !jobProcessingStartTimes.has(job.job_id)) {
        jobProcessingStartTimes.set(job.job_id, currentTime);
      }
    });

    const timedOutJobs = batchStatus.jobs.filter((job) => {
      if (job.status === 'processing') {
        const processingStartTime = jobProcessingStartTimes.get(job.job_id);
        if (processingStartTime) {
          return currentTime - processingStartTime > perJobTimeout;
        }
      }
      return false;
    });

    const completedJobs = batchStatus.jobs.filter((job) => job.status === 'completed');
    const failedJobs = batchStatus.jobs.filter((job) => job.status === 'failed');
    const totalFinished = completedJobs.length + failedJobs.length + timedOutJobs.length;

    if (totalFinished === batchStatus.jobs.length) {
      const allResults = completedJobs.filter((job) => job.result).map((job) => job.result);

      if (allResults.length > 0) {
        if (failedJobs.length > 0) {
          console.warn(
            `${failedJobs.length} job(s) failed:`,
            failedJobs.map((j) => j.job_id),
          );
        }
        if (timedOutJobs.length > 0) {
          console.warn(
            `${timedOutJobs.length} job(s) timed out after 5 minutes:`,
            timedOutJobs.map((j) => j.job_id),
          );
        }

        if (typeof allResults[0] === 'string') {
          return allResults.join(' ');
        }
        return allResults;
      }

      const errorMessage = `All jobs failed or timed out. Failed: ${failedJobs.length}, Timed out: ${timedOutJobs.length}`;
      throw new Error(errorMessage);
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  throw new Error('Batch job polling timeout');
}

export const bySimstudio = {
  executeSimStudioWorkflow,
  pollJobStatus,
  pollBatchJobStatus,
};
